import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  NeedHelp as UIFoundationsNeedHelp,
  NeedHelpContact
} from '@dcxwam/dcx-wam-ui-foundations'
import NeedHelpEditConfig, {
  hasNeedHelpContact,
  hasRequiredNeedHelpProps
} from './NeedHelpEditConfig'
import { NeedHelpContactModel, NeedHelpModelProps } from './types'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import {
  WithAutomaticTestIdsType,
  withAutomaticTestIds
} from '../../../utils/aem/with-automatic-test-ids'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'

/**
 * Narrows the optional AEM contact entries to the UI Foundations shape, which
 * types `value` as required. Entries without a value have nothing to render.
 */
const toFoundationsContacts = (
  contacts?: NeedHelpContactModel[]
): NeedHelpContact[] =>
  (contacts ?? []).reduce<NeedHelpContact[]>((items, contact) => {
    const value = contact?.value
    if (typeof value !== 'string' || value.trim().length === 0) {
      return items
    }
    items.push({
      label: contact.label,
      value,
      linkType: contact.linkType
    })
    return items
  }, [])

const NeedHelp = withComponentRefresh(
  withAutomaticTestIds(
    (
      props: WithAutomaticTestIdsType<
        EditableComponentPropsType<NeedHelpModelProps>
      >
    ) => {
      const {
        title,
        contacts,
        phoneNumberLabel,
        phoneNumber,
        schedule,
        disclaimer,
        id,
        ...rest
      } = props.model
      const { getDataTestIdObject } = props
      const editableProps = useExtractEditableProps(props, rest)

      // Grouped so the edit config's type guard can narrow both required
      // props at once, without casts or non-null assertions.
      const requiredModel = { title, schedule }

      return (
        <EditableComponent {...editableProps}>
          {hasRequiredNeedHelpProps(requiredModel) &&
          hasNeedHelpContact(props.model) ? (
            <UIFoundationsNeedHelp
              {...requiredModel}
              contacts={toFoundationsContacts(contacts)}
              phoneNumberLabel={phoneNumberLabel}
              phoneNumber={phoneNumber}
              id={id}
              disclaimer={disclaimer}
              {...getDataTestIdObject('need-help', id)}
            />
          ) : (
            NeedHelpEditConfig.emptyLabel
          )}
        </EditableComponent>
      )
    }
  )
)

const NeedHelpWithErrorBoundary = withErrorBoundary(NeedHelp, {
  componentName: 'NeedHelp',
  fallback: <ErrorFallback componentName="NeedHelp" />
})

export default NeedHelpWithErrorBoundary
